import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { connect, Dispatch } from 'react-redux';
import { ICenterBillingRecord, IInvoiceCreditPaymentsReport } from '@nkr/models/index';
import { OrganType } from '@nkr/enums/index';
import { downloadFile } from '@nkr/services/download-file';
import { IApplicationState, AppActionCreators, BillingActionCreators, IBillingCenterReportResponse, IInvoiceCreditPaymentsResponse, ReportActionCreators, InvoicingActionCreators } from '@nkr/store/index';
import { ColumnApi, GridApi, GridReadyEvent, ProcessCellForExportParams, ExcelExportParams, ColumnWidthCallbackParams } from 'ag-grid-community';
import { Button, ButtonToolbar, ToggleButtonGroup, ToggleButton, FormControl, Well } from 'react-bootstrap';
import Grid from './grid';

interface IOwnProps {
    centerId: number,
    centerAlias: string
}

interface IStateProps {
    organType: OrganType
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getInvoiceCreditPaymentsReport: (centerId: number, startDate: string, endDate: string, organType: OrganType) => Promise<IInvoiceCreditPaymentsResponse>,
    viewInvoice: (invoiceNumber: number) => Promise<any>;
}

type TProps = IStateProps & IDispatchProps & IOwnProps

interface IState {
    list: IInvoiceCreditPaymentsReport[],
    radioFilter: number,
    invoiceStartDate: string,
    invoiceEndDate: string,
    centerId: number,
    filteredList: IInvoiceCreditPaymentsReport[],
    useFilter: boolean,
    payableType: string,   // new
    organTypeFilter: string // new
}

interface IClearState {
    radioFilter: number,
    invoiceStartDate: string,
    invoiceEndDate: string,
    payableType: string,    // new
    organTypeFilter: string // new
}

const initialClearState: IClearState = {
    radioFilter: 1,
    invoiceStartDate: moment().subtract(12, 'months').startOf('month').format('YYYY-MM-DD'),
    invoiceEndDate: moment().format('YYYY-MM-DD'),
    payableType: '',    // new - empty means "All"
    organTypeFilter: '' // new - empty means "All"
}

const initialState: IState = {
    ...initialClearState,
    list: [],
    centerId: 0,
    filteredList: [],
    useFilter: false
}

class AccountsPayableTotal extends React.Component<TProps, {}> {
    readonly state = initialState;

    gridApi: GridApi = null;
    gridColumnApi: ColumnApi = null;

    onGridReady = (e: GridReadyEvent) => {
        this.gridApi = e.api;
        this.gridColumnApi = e.columnApi;

        e.columnApi.applyColumnState({
            state: [
                { colId: 'centerHasLatePaymentsInLastYear', sort: 'asc' }
            ]
        });
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        this.props.showLoading('Getting report data, one moment please...').then(() => {
            this.props.getInvoiceCreditPaymentsReport(this.props.centerId, this.state.invoiceStartDate, this.state.invoiceEndDate, this.props.organType).then((response) => {
                if (response.data.success) {
                    let list = response.data.results;
                    let filteredList = this.filterRecords(list, this.state.radioFilter); // 1 → open
                    this.setState({ list, filteredList }, () => this.props.hideLoading());
                } else {
                    this.props.hideLoading();
                }
            });
        });
    };

    handleRadioFilterChange = (radioType: number) => {
        this.setState({ radioFilter: radioType }, () => {
            this.handleFilteredData();
        });
    }

    handleDateChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;
        this.setState({ [name]: value });
    }

    // new
    handleDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.currentTarget;
        this.setState({ [name]: value } as any);
    }

    handleFilteredData = () => {
        const filteredList = this.filterRecords(this.state.list, this.state.radioFilter);
        this.setState({ useFilter: true, filteredList });
    };

    filterRecords = (list: IInvoiceCreditPaymentsReport[], radioFilter: number) => {
        // Paid Invoice
        if (radioFilter === 0) {
            const startDate = moment(this.state.invoiceStartDate);
            const endDate = moment(this.state.invoiceEndDate);
            return list.filter(x => {
                const invoiceDate = moment({ year: x.invoiceYear, month: x.invoiceMonth - 1, day: 1 });
                return invoiceDate.isSameOrAfter(startDate, 'month')
                    && invoiceDate.isSameOrBefore(endDate, 'month')
                    && x.amountPaid > 0;
            });
        }
        // Open Invoice Payable (default, radioFilter == 1)
        return list.filter(x => x.amountRemaining > 0);
    };

    viewInvoice = (invoiceNumber) => {
        this.props.showLoading("Downloading Invoice").then(() => {
            this.props.viewInvoice(invoiceNumber).then((response: any) => {
                downloadFile(response.data, `invoice-${invoiceNumber}.pdf`, 'application/pdf');
                this.props.hideLoading();
            })
        })
    };

    handleExport = () => {
        let keys = this.gridColumnApi.getAllColumns().map(x => x.getColId());

        let params: ExcelExportParams = {
            columnGroups: true,
            fileName: `accounts-payable-total.xlsx`,
            columnKeys: keys,
            columnWidth: (params: ColumnWidthCallbackParams) => {
                let colDef = params.column.getColDef();
                return colDef.width;
            },
            processCellCallback: (params: ProcessCellForExportParams) => {
                return params.value;
            },
        };

        this.gridApi.exportDataAsExcel(params);
    }

    handleClear = () => {
        this.setState(initialClearState, () => {
            this.handleFilteredData();
        });
    }


    render = () => {
        const filteredData = this.state.filteredList;
        const showDateFilter = [0].includes(this.state.radioFilter);

        return (
            <>
                <div className='container-fluid'>
                    <div style={{ padding: 10, borderWidth: '0px 1px 1px 1px', borderStyle: 'solid', borderColor: '#ddd', borderBottomLeftRadius: 4, borderBottomRightRadius: 4 }}>
                        <Well bsSize='sm' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>

                                {/* Existing radio filter */}
                                <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                                    <div style={{ marginRight: 5 }}>Filters:</div>
                                    <ButtonToolbar>
                                        <ToggleButtonGroup type="radio" name="options" value={this.state.radioFilter}>
                                            <ToggleButton className="btn-bg" value={1} onClick={() => this.handleRadioFilterChange(1)}>Open Invoice Payable</ToggleButton>
                                            <ToggleButton className="btn-bg" value={0} onClick={() => this.handleRadioFilterChange(0)}>Paid Invoice</ToggleButton>
                                        </ToggleButtonGroup>
                                    </ButtonToolbar>
                                </div>

                                {showDateFilter && (
                                    <>
                                        <div style={{ display: 'flex', alignItems: 'center', marginLeft: 50, marginRight: 10 }}>  {/* <-- added marginLeft */}
                                            <div style={{ marginRight: 5, whiteSpace: 'nowrap' }}>Invoice Start Date:</div>
                                            <FormControl
                                                type='date'
                                                name='invoiceStartDate'
                                                value={this.state.invoiceStartDate}
                                                onChange={this.handleDateChange}
                                            />
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                                            <div style={{ marginRight: 5, whiteSpace: 'nowrap' }}>Invoice End Date:</div>
                                            <FormControl
                                                type='date'
                                                name='invoiceEndDate'
                                                value={this.state.invoiceEndDate}
                                                onChange={this.handleDateChange}
                                            />
                                        </div>

                                        <div>
                                            <Button bsStyle={'primary'} onClick={this.handleFilteredData}>Search</Button>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div style={{ display: 'flex', marginLeft: '50px' }}>
                                <Button onClick={this.handleClear} style={{ marginRight: 5 }}>Clear</Button>
                                <Button bsStyle={'success'} onClick={this.handleExport}>Export to Excel</Button>
                            </div>
                        </Well>

                        <Grid data={filteredData} centerId={this.props.centerId} onViewClicked={this.viewInvoice} onGridReady={this.onGridReady} />
                    </div>
                </div>
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        organType: appState.app.site.organType
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => dispatch(AppActionCreators.showLoadingAsync(message)),
        hideLoading: () => dispatch(AppActionCreators.hideLoading()),
        getInvoiceCreditPaymentsReport: (payableCenterId, startDate, endDate, organType) => {
            return dispatch(ReportActionCreators.getInvoiceCreditPaymentsReport(payableCenterId, startDate, endDate, organType));
        },
        viewInvoice: (invoiceNumber) => {
            return dispatch(InvoicingActionCreators.viewInvoice(invoiceNumber));
        }

    }
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(AccountsPayableTotal);